if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', (event) => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
            event.currentTarget.focus({ preventScroll: true });
          });
        });

        this.elements.thumbnails.addEventListener(
          'scroll',
          debounce(() => {
            if (this.isThumbnailTrackScrolling) return;
            this.updateThumbnailAccessibility();
          }, 100),
          { passive: true }
        );

        this.elements.thumbnails.addEventListener('focusin', (event) => {
          const thumbnailSlide = event.target.closest('.thumbnail-list__item.slider__slide');
          if (!thumbnailSlide || this.isThumbnailSlideVisible(thumbnailSlide)) return;

          this.scrollThumbnailIntoView(thumbnailSlide);
          this.updateThumbnailAccessibility();
        });

        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => {
            button.addEventListener('click', this.onThumbnailArrowClick.bind(this), { capture: true });
          });
      }

      this.updateSlideAccessibility();
    }

    onSlideChanged(event) {
      this.updateSlideAccessibility();
      if (!this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);
      this.updateSlideAccessibility();

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('.thumbnail').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('.thumbnail').setAttribute('aria-current', 'true');

      if (!this.isThumbnailSlideRendered(thumbnail)) return;
      if (this.isThumbnailSlideVisible(thumbnail)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
    }

    onThumbnailArrowClick(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const thumbnailSlides = this.getNavigableThumbnailSlides();
      const visibleSlides = thumbnailSlides.filter((slide) => this.isThumbnailSlideVisible(slide));
      if (!visibleSlides.length) return;

      const direction = event.currentTarget.name;
      const boundarySlide = direction === 'next'
        ? visibleSlides[visibleSlides.length - 1]
        : visibleSlides[0];
      const boundaryIndex = thumbnailSlides.indexOf(boundarySlide);
      const targetIndex = direction === 'next' ? boundaryIndex + 1 : boundaryIndex - 1;
      const targetSlide = thumbnailSlides[targetIndex];
      if (!targetSlide) return;

      this.isThumbnailTrackScrolling = true;
      this.waitForThumbnailScrollEnd(() => {
        this.isThumbnailTrackScrolling = false;
        this.elements.thumbnails.update();
        this.updateThumbnailAccessibility();

        const targetButton = targetSlide.querySelector('.thumbnail');
        if (targetButton) targetButton.focus({ preventScroll: true });
      });
      this.scrollThumbnailIntoView(targetSlide, direction);
    }

    scrollThumbnailIntoView(thumbnailSlide, direction) {
      const slider = this.elements.thumbnails.slider;
      if (!slider) return;

      const alignToEnd = direction === 'previous' || thumbnailSlide.offsetLeft < slider.scrollLeft;
      slider.scrollLeft = alignToEnd
        ? Math.max(0, thumbnailSlide.offsetLeft + thumbnailSlide.clientWidth - slider.clientWidth)
        : thumbnailSlide.offsetLeft;
    }

    waitForThumbnailScrollEnd(callback) {
      const slider = this.elements.thumbnails.slider;
      if (!slider) {
        callback();
        return;
      }

      if (this.cancelThumbnailScrollWait) this.cancelThumbnailScrollWait();

      this.thumbnailScrollRequestId = (this.thumbnailScrollRequestId || 0) + 1;
      const requestId = this.thumbnailScrollRequestId;
      let settleTimer;
      let fallbackTimer;
      let finished = false;

      const cleanup = () => {
        slider.removeEventListener('scroll', onScroll);
        window.clearTimeout(settleTimer);
        window.clearTimeout(fallbackTimer);
      };

      const finish = () => {
        if (finished) return;
        finished = true;
        cleanup();

        if (requestId !== this.thumbnailScrollRequestId) return;

        this.cancelThumbnailScrollWait = null;
        callback();
      };

      const onScroll = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(finish, 150);
      };

      this.cancelThumbnailScrollWait = () => {
        if (finished) return;
        finished = true;
        cleanup();
      };

      slider.addEventListener('scroll', onScroll, { passive: true });
      fallbackTimer = window.setTimeout(finish, 1200);
    }

    getNavigableThumbnailSlides() {
      return Array.from(
        this.elements.thumbnails.querySelectorAll('.thumbnail-list__item.slider__slide')
      ).filter((thumbnailSlide) => this.isThumbnailSlideRendered(thumbnailSlide));
    }

    isThumbnailSlideRendered(thumbnailSlide) {
      return thumbnailSlide.clientWidth > 0 && thumbnailSlide.getClientRects().length > 0;
    }

    isThumbnailSlideVisible(thumbnailSlide) {
      const slider = this.elements.thumbnails?.slider;
      if (!slider || !this.isThumbnailSlideRendered(thumbnailSlide)) return false;

      const boundaryTolerance = 1;
      const viewportLeft = slider.scrollLeft;
      const viewportRight = viewportLeft + slider.clientWidth;
      const thumbnailLeft = thumbnailSlide.offsetLeft;
      const thumbnailRight = thumbnailLeft + thumbnailSlide.clientWidth;

      return thumbnailLeft >= viewportLeft - boundaryTolerance &&
        thumbnailRight <= viewportRight + boundaryTolerance;
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    updateSlideAccessibility() {
      const slides = this.elements.viewer.querySelectorAll('[data-media-id]');

      slides.forEach((slide) => {
        const isActive = slide.classList.contains('is-active');
        const focusableElements = slide.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');

        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        focusableElements.forEach((element) => {
          if (isActive) {
            if (element.hasAttribute('data-original-tabindex')) {
              element.setAttribute('tabindex', element.getAttribute('data-original-tabindex'));
              element.removeAttribute('data-original-tabindex');
            } else if (element.getAttribute('tabindex') === '-1') {
              element.removeAttribute('tabindex');
            }
          } else {
            if (element.hasAttribute('tabindex') && !element.hasAttribute('data-original-tabindex')) {
              element.setAttribute('data-original-tabindex', element.getAttribute('tabindex'));
            }
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.updateThumbnailAccessibility();
    }

    updateThumbnailAccessibility() {
      if (!this.elements.thumbnails) return;

      const thumbnailSlides = this.elements.thumbnails.querySelectorAll('.thumbnail-list__item.slider__slide');

      thumbnailSlides.forEach((thumbnailSlide) => {
        const thumbnailButton = thumbnailSlide.querySelector('.thumbnail');
        if (!thumbnailButton) return;

        const isRendered = this.isThumbnailSlideRendered(thumbnailSlide);
        thumbnailSlide.setAttribute('aria-hidden', isRendered ? 'false' : 'true');

        if (isRendered) {
          thumbnailButton.removeAttribute('tabindex');
        } else {
          thumbnailButton.setAttribute('tabindex', '-1');
        }
      });
    }

  });
}
